import { t } from '../i18n/index.ts';

/**
 * Formatting for a broadcast control room.
 *
 * Western digits throughout (CLAUDE.md): unambiguous in a technical context and
 * they align in a monospace column. The API always sends UTC; converting to the
 * user's timezone happens only here.
 */

/**
 * `en-GB` gives 24-hour time with Western digits. Using an Arabic locale here
 * would produce Arabic-Indic digits, which the brief rules out.
 */
const DIGIT_LOCALE = 'en-GB';

export function formatClock(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleTimeString(DIGIT_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
    hour12: false,
  });
}

export function formatClockSeconds(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleTimeString(DIGIT_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone,
    hour12: false,
  });
}

export function formatDateKey(iso: string, timeZone: string): string {
  // Sortable and stable; used to group blocks under a date divider.
  return new Date(iso).toLocaleDateString('en-CA', { timeZone });
}

export function formatDateLabel(iso: string, timeZone: string, now = new Date()): string {
  const key = formatDateKey(iso, timeZone);
  if (key === formatDateKey(now.toISOString(), timeZone)) return t('today');
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (key === formatDateKey(yesterday.toISOString(), timeZone)) return t('yesterday');
  return new Date(iso).toLocaleDateString(DIGIT_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone,
  });
}

/** `HH:MM:SS` for a position inside a recording. */
export function formatPosition(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/** A human span, for "deleted in 3h 12m". */
export function formatRemaining(msRemaining: number): string {
  const totalMinutes = Math.max(0, Math.floor(msRemaining / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) return t('time.hours_minutes', { hours, minutes });
  if (totalMinutes > 0) return t('time.minutes', { minutes: totalMinutes });
  return t('time.seconds', { seconds: Math.max(0, Math.floor(msRemaining / 1000)) });
}

/**
 * The length of a clip, for the producer deciding whether to download it.
 *
 * Raw seconds are unreadable past a minute or so — a 27-minute selection shows
 * as "1656.0", which nobody can judge at a glance. Tenths are kept because
 * this is also the number being trimmed against.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const whole = Math.floor(total % 60);
  const tenths = Math.floor((total * 10) % 10);

  const secondsPart = `${String(whole).padStart(2, '0')}.${tenths}`;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${secondsPart}`;
  }
  return `${minutes}:${secondsPart}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
}

/** Fraction of a block that holds audio, for the coverage bar. */
export function coverageFraction(coveredSec: number, blockSeconds: number): number {
  if (blockSeconds <= 0) return 0;
  return Math.max(0, Math.min(1, coveredSec / blockSeconds));
}

/**
 * The ceiling a `too_long` refusal came with, as minutes and seconds.
 *
 * The server states its own limit in the error rather than the page carrying a
 * copy, so a limit changed in configuration is a limit the page reports
 * correctly without being rebuilt.
 */
export function maxFromError(details: Record<string, unknown>): string {
  const seconds = Number(details['max_seconds']);
  return Number.isFinite(seconds) && seconds > 0 ? formatDuration(seconds) : '';
}
